import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, TrendingDown, Trash2, ArrowRight, Loader2, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  getSavedEmail, setSavedEmail, listWatchlist, unsaveVehicle,
  listNotifications, markNotificationsRead,
} from '../lib/watchlist';

function money(n) { return `$${(n || 0).toLocaleString()}`; }

export default function Watchlist() {
  const [email, setEmail] = React.useState(() => getSavedEmail() || '');
  const [items, setItems] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [unread, setUnread] = React.useState(0);

  const load = async (e) => {
    if (e) e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error('Enter a valid email');
    setSavedEmail(email);
    setLoading(true);
    try {
      const [it, notif] = await Promise.all([listWatchlist(email), listNotifications(email)]);
      setItems(it);
      setUnread(notif.unread_count);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { if (getSavedEmail()) load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    try {
      await unsaveVehicle(email, id);
      setItems((prev) => prev.filter((v) => v.id !== id));
      toast.success('Removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const markAllRead = async () => {
    await markNotificationsRead(email);
    setUnread(0);
    setItems((prev) => (prev || []).map((v) => ({ ...v, price_drop: undefined })));
    toast.success('Marked all as read');
  };

  const input = 'w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]';

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14" data-testid="page-watchlist">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Saved for later</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">Your garage.</h1>
          <p className="mt-2 text-zinc-400 text-sm">Hearted listings. Price drops. All in one place.</p>
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            className="hairline hover:border-[#ff2a2a] text-white text-xs font-mono uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5"
            data-testid="mark-read"
          >
            <MailCheck className="w-3.5 h-3.5" /> Mark all read ({unread})
          </button>
        )}
      </div>

      <form onSubmit={load} className="mt-8 am-card p-5 grid md:grid-cols-[1fr_auto] gap-3" data-testid="watchlist-lookup">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email you saved with"
          className={input}
          data-testid="watchlist-email"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-[#ff2a2a] hover:bg-[#e01f1f] disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
          data-testid="watchlist-load"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
          View saved
        </button>
      </form>

      {items === null ? null : items.length === 0 ? (
        <div className="mt-8 am-card p-16 text-center" data-testid="watchlist-empty">
          <Heart className="w-10 h-10 mx-auto text-zinc-700" />
          <div className="mt-4 font-display text-2xl uppercase tracking-tight">Nothing saved yet</div>
          <p className="mt-2 text-zinc-400 text-sm max-w-md mx-auto">
            Tap the heart on any listing to save it. If the seller drops the price, you&rsquo;ll see it here.
          </p>
          <Link to="/marketplace" className="mt-6 inline-block bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-6 py-3 transition-colors">
            Browse Marketplace
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="watchlist-grid">
          {items.map((v) => (
            <article key={v.id} className="am-card overflow-hidden group flex flex-col relative" data-testid={`watchlist-card-${v.id}`}>
              {v.price_drop && (
                <div className="absolute top-3 left-3 z-10 bg-emerald-500 text-black px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1" data-testid={`price-drop-${v.id}`}>
                  <TrendingDown className="w-3 h-3" /> Price dropped {money(v.price_drop.delta)}
                </div>
              )}
              <Link to={`/vehicles/${v.id}`} className="block relative aspect-[4/3] overflow-hidden">
                {v.photos && v.photos[0] ? (
                  <img src={v.photos[0]} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-[#131313]" />
                )}
                <div className="absolute bottom-3 right-3 font-mono text-lg text-white bg-black/60 px-2.5 py-1 backdrop-blur-md">
                  {money(v.price)}
                </div>
              </Link>
              <div className="p-4 flex-1 flex flex-col">
                <Link to={`/vehicles/${v.id}`} className="text-white font-semibold hover:text-[#ff2a2a] transition-colors">{v.title}</Link>
                {v.price_drop && (
                  <div className="mt-2 text-xs text-emerald-400 font-mono">
                    <span className="line-through text-zinc-500 mr-1">{money(v.price_drop.old_price)}</span>
                    → {money(v.price_drop.new_price)}
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Link
                    to={`/vehicles/${v.id}`}
                    className="hairline hover:border-[#ff2a2a] hover:text-white text-zinc-200 text-xs font-semibold py-2.5 transition-colors inline-flex items-center justify-center gap-1.5"
                    data-testid={`watchlist-view-${v.id}`}
                  >
                    View <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => remove(v.id)}
                    className="hairline hover:border-red-500 hover:text-red-400 text-zinc-400 text-xs font-semibold py-2.5 transition-colors inline-flex items-center justify-center gap-1.5"
                    data-testid={`watchlist-remove-${v.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
